import {
  type PortableTextTypeComponentOptions,
  toHTML,
} from '@portabletext/to-html'
import h from 'hyperscript'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getSanityImageUrl } from '@lib/image'
import { getPageUrl, PageType } from './routes'
import { SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import {
  SanityBlogPostBody,
  SanityModuleType,
} from '@data/sanity/queries/types/modules'
import {
  SanityContentInfoBox,
  SanityContentQuote,
} from '@data/sanity/queries/types/content'
import { SanityImageFragment } from '@data/sanity/queries/types/image'
import { TypedObject } from '@portabletext/types'

interface AuthorSchema {
  '@type': 'Person' | 'Organization'
  name?: string
  url?: string
}

interface ImageSchema {
  '@type': 'ImageObject'
  '@id': string
  url: string
  width?: string
  height?: string
}

interface BlogPostSchema {
  '@context': 'https://schema.org'
  '@type': 'BlogPosting'
  url: string
  headline: string
  datePublished: Date
  dateModified: Date
  author?: string | AuthorSchema
  keywords?: string[]
  abstract?: string
  articleBody?: string
  thumbnailUrl?: string
  image?: string | string[] | ImageSchema | ImageSchema[]
}

export const getBlogPostSchema = (
  {
    title: headline,
    excerpt: abstract,
    slug,
    author,
    date,
    modifiedDate,
    categories,
    coverImage,
    modules,
    seo,
  }: SanityBlogPostPage,
  site: SanitySiteFragment,
) => {
  const domain = site.settings.siteURL.replace(/\/+$/g, '')
  const blogPostUrl = getPageUrl(PageType.BLOG_POST, slug)

  const blogPostBodyModule = modules?.find(
    (module) => module._type == SanityModuleType.BLOG_POST_BODY,
  ) as SanityBlogPostBody | undefined

  const articleBody = blogPostBodyModule
    ? toHTML(
        blogPostBodyModule.content?.filter((item) => {
          return ['block', 'quote', 'figure', 'infoBox'].includes(item._type)
        }) as unknown as TypedObject[],
        {
          components: {
            types: {
              quote: ({
                value,
              }: PortableTextTypeComponentOptions<SanityContentQuote>) => {
                return h(
                  'figure',
                  {},
                  h('blockquote', value.text),
                  h('figcaption', value.author),
                ).outerHTML
              },
              figure: ({
                value,
              }: PortableTextTypeComponentOptions<SanityImageFragment>) => {
                const imageUrl = getSanityImageUrl(value, {
                  width: value.asset?.metadata?.dimensions?.width,
                  height: value.asset?.metadata?.dimensions?.height,
                  fitMax: true,
                  autoFormat: true,
                })

                return h('img', {
                  src: imageUrl,
                  alt: value.alt,
                }).outerHTML
              },
              infoBox: ({
                value,
              }: PortableTextTypeComponentOptions<SanityContentInfoBox>) => {
                return h('div', {}, h('h4', value.title), h('p', value.content))
                  .outerHTML
              },
            },
          },
        },
      )
    : ''

  const imageUrl = getSanityImageUrl(coverImage, {
    width: coverImage?.dimensions.width,
    height: coverImage?.dimensions.height,
    fitMax: true,
    autoFormat: true,
  })
  const thumbnailUrl = getSanityImageUrl(seo?.shareGraphic, {
    // Width and height taken from `HeadSeo` component
    width: 1200,
    height: 630,
  })

  const schema: BlogPostSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    abstract,
    articleBody,
    url: `${domain}${blogPostUrl}`,
    datePublished: new Date(date),
    dateModified: new Date(modifiedDate),
    keywords: categories?.map((category) => category.name),
    thumbnailUrl,
  }

  if (author) {
    schema.author = {
      '@type': 'Person',
      name: author.name,
      url: `${domain}${getPageUrl(PageType.BLOG_AUTHOR, author.slug)}`,
    }
  }

  if (imageUrl) {
    const imageWidth = coverImage?.dimensions.width
    const imageHeight = coverImage?.dimensions.height
    const image: ImageSchema = {
      '@type': 'ImageObject',
      '@id': imageUrl,
      url: imageUrl,
    }

    if (imageWidth) {
      image.width = `${imageWidth}`
    }
    if (imageHeight) {
      image.height = `${imageHeight}`
    }

    schema.image = image
  }

  return JSON.stringify(schema)
}
